<script setup>
import { inject, ref, onMounted, nextTick } from 'vue'
import { watchThrottled } from '@vueuse/core'
import { isMobileDevice } from '@/utils'
import VoteCard from '../VoteCard/index.vue'

defineProps({
  mode: { type: String, default: 'vote' },
  onlyShow: { type: Boolean, default: true }, // 只展示
  list: { type: Array, default: () => [] },
  data: {
    type: Object,
    default: () => {
      return {}
    }
  }
})

const emit = defineEmits(['rowHeightChange', 'select'])

// 一排4個時 顯示8排，32個選項為一頁 1920 - 1120
// 一排3個時 顯示11排，33個選項為一頁 1119 - 1024 - 500
// 一排2個時 顯示16排，32個選項為一頁 499 - 320
// 一排1個時 32個選項為一頁 （纯文本）
// option_style 投票选项样式：1-文字（默认），2-图片，3-图片+文字

const windowWidth = inject('width')
const size = inject('size')
const showViewer = inject('showViewer')
const onImgclick = ({ src }) => {
  showViewer(src)
}

// start - 计算列表所有选项的高度取最高的那个
const paneRef = ref()
const maxRowHeight = ref()
const handleMaxHeight = () => {
  try {
    const rows = Array.from(paneRef.value.children)
    const maxHeight = Math.max(...rows.map((row) => row.offsetHeight))
    maxRowHeight.value = maxHeight
    nextTick(() => {
      // console.log('🚀 ~ ~ rowHeight:', maxHeight)
      emit('rowHeightChange')
    })
  } catch (error) {
    console.warn('handleMaxHeight:', error)
  }
}

const ios = () => {
  const userAgent = window.navigator.userAgent.toLowerCase()
  const find = (needle) => userAgent.indexOf(needle) !== -1
  return find('iphone') || find('ipad')
}
const portrait = () => {
  if (screen.orientation && Object.prototype.hasOwnProperty.call(window, 'onorientationchange')) {
    return screen.orientation.type.indexOf('portrait') !== -1
  }
  if (ios() && Object.prototype.hasOwnProperty.call(window, 'orientation')) {
    return Math.abs(window.orientation) !== 90
  }
  return window.innerHeight / window.innerWidth > 1
}
const landscape = () => {
  if (screen.orientation && Object.prototype.hasOwnProperty.call(window, 'onorientationchange')) {
    return screen.orientation.type.indexOf('landscape') !== -1
  }
  if (ios() && Object.prototype.hasOwnProperty.call(window, 'orientation')) {
    return Math.abs(window.orientation) === 90
  }
  return window.innerHeight / window.innerWidth < 1
}

const handle = () => {
  maxRowHeight.value = null
  nextTick(() => {
    handleMaxHeight()
  })
}

watchThrottled(
  windowWidth,
  () => {
    // 监听横竖屏
    if (isMobileDevice() && (portrait() || landscape())) {
      setTimeout(() => {
        handle()
      }, 600)
    } else {
      handle()
    }
  },
  { throttle: 300 }
)
// end - 计算列表所有选项的高度取最高的那个

onMounted(() => {
  handleMaxHeight()
})
</script>

<template>
  <div
    ref="paneRef"
    class="tvb-vote-slide-pane"
    :class="'type' + data.option_style"
    :style="maxRowHeight && `--max-row-height: ${maxRowHeight}px`"
  >
    <template v-for="(item, i) in list" :key="i">
      <vote-card
        :id="item.option_id"
        :size="size"
        :title="data.option_style === 2 ? '' : item.name_tc?.trim()"
        :subtitle="data.option_style === 2 ? '' : item.name_en?.trim()"
        :desc="data.option_style === 2 ? '' : item.description_tc?.trim()"
        :desc2="data.option_style === 2 ? '' : item.description_en?.trim()"
        :link="item.option_link?.trim()"
        :linkText="item.option_link_text?.trim()"
        :onlyShow="onlyShow"
        :img="data.option_style === 1 ? null : item.option_image_info"
        :alt="item.option_image_alt_text"
        :coverRatio="data.option_image_size"
        :selected="mode === 'result' ? false : item.selected"
        :selectedText="item.voted ? '已投票' : '已選'"
        @select="emit('select', item.option_id)"
        @imgclick="onImgclick({ src: item.option_image_info.medium })"
      ></vote-card>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tvb-vote-slide-pane {
  --gap: 16px;
  --columns: 4;
  gap: var(--gap);
  display: grid;
  grid-template-columns: repeat(var(--columns), 1fr);
  grid-auto-rows: var(--max-row-height); /* 行高将设置为最高子项的高度 */

  @media (max-width: 1119px) and (min-width: 500px) {
    --columns: 3;
  }
  @media (max-width: 767px) {
    --gap: 8px;
  }
  @media (max-width: 499px) {
    --gap: 8px;
    --columns: 2;
    // 纯文本
    &.type1 {
      --columns: 1;
      grid-auto-rows: auto;
    }
  }
}
</style>
